
import React, { ChangeEvent, useState, useEffect, useMemo, useRef, useLayoutEffect } from "react";
import { Input, Button, Popup, InputOnChangeData, Label, Ref } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { DateInput as SUICalendar } from "semantic-ui-calendar-react";

import { ensureFreshDateInstance, resetTime } from "./DateTimePicker.utils";
import ErrorBoundary from "../../../../../sg-core/components/ErrorBoundary";
import { DATE_FORMAT } from "../../../../constants/formats";
import Validate, { ERangeError, getDateRangeErrorMessage } from "./DateTimePicker.validation"
import { useOnClickOutside } from "../../../../../sg-hooks/useOnClickOutside";
import moment, { Moment } from 'moment'
import "moment/locale/fi"
import "moment/locale/es-us"

export interface IDateInput {
    minDate?: Date;
    maxDate?: Date;
    format?: string;
    errorMessage?: string;
    lang?: "fi" | "en" | string;

    value?: string;
    onChange: (date: string | undefined) => void;
}


export const DateInput: React.FC<IDateInput> = ({
    format: dateFormat = DATE_FORMAT,
    value,
    onChange,
    errorMessage,
    lang = "fi",
    ...props
}) => {
    const { t } = useTranslation()

    const minDate = props.minDate ? resetTime(ensureFreshDateInstance(props.minDate) as any) : undefined
    const maxDate = props.maxDate ? resetTime(ensureFreshDateInstance(props.maxDate) as any) : undefined

    const inputRef = useRef<HTMLInputElement>(null);

    const [inputError, setInputError] = useState<string | undefined>();

    const [isOpen, setOpen] = useState(false);

    useLayoutEffect(() => { moment.locale(lang); }, [lang])

    useEffect(() => {
        const inputElement = inputRef.current?.getElementsByTagName("INPUT")[0] as HTMLInputElement | undefined;
        if (inputElement && typeof value !== undefined && (inputElement.value || "") !== value) {
            inputElement.value = value || ""
        }
    }, [value])


    const updateInput = (inputValue?: string, strict = false) => {
        if (inputError) {
            setInputError(undefined)
        };

        if (!inputValue) {
            return onChange(undefined);
        };

        const inputTextSeemsCompleteDate = Validate.validateDateInput(inputValue, strict)
        if (!inputTextSeemsCompleteDate) {
            return
        };

        const rangeError = Validate.validateDateRange(inputValue, { minDate, maxDate });
        if (rangeError) {
            const errorMessage = getDateRangeErrorMessage(rangeError, { minDate, maxDate }, t);
            setInputError(errorMessage);
            if (rangeError === ERangeError.InvalidDate) {
                onChange(undefined);
            }
            onChange(inputValue);
            return
        }

        onChange(inputValue);
    }


    const _onCalendarChange = (e: React.SyntheticEvent<HTMLElement, Event>, data: any) => {
        updateInput(data.value);
        setOpen(false);
    }

    const _onInputChange = (event: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        inputError && setInputError(undefined);
        const value = event.target.value;
        updateInput(value, true);
    };


    const _onOutsideClick = () => {
        const inputElement = inputRef.current?.getElementsByTagName("INPUT")[0] as HTMLInputElement | undefined;
        if (inputElement && inputElement === document.activeElement) {
            inputError && setInputError(undefined);
            updateInput(inputElement?.value);
        }
    };

    useOnClickOutside(inputRef, _onOutsideClick);

    return (
        <div style={{ position: "relative", width: "100%" }}>
            <Popup
                on="click"
                open={isOpen}
                position="bottom right"
                onClose={() => setOpen(false)}
                style={{ padding: 0, zIndex: 9999999 }}
                trigger={(
                    <Ref innerRef={inputRef}>
                        <Input
                            placeholder="pp.kk.vvvv"
                            onChange={_onInputChange}
                            //   value={value}
                            error={Boolean(inputError)}
                            label={
                                <Button
                                    ref={null}
                                    type="button"
                                    icon="calendar"
                                    onClick={() => setOpen(open => !open)}
                                />
                            }
                            labelPosition="right"
                        />
                    </Ref>
                )}
            >
                <ErrorBoundary fallback={<div> Error:  failed to display date picker </div>}>
                    <SUICalendar
                        inline
                        localization={lang}
                        minDate={minDate ? minDate : undefined}
                        maxDate={maxDate ? maxDate : undefined}
                        dateFormat={dateFormat.replace("dd", "DD")}
                        marked={(!maxDate && !minDate) ? [new Date()] : undefined}
                        value={value || ""}
                        onChange={_onCalendarChange}
                    />
                </ErrorBoundary>
            </Popup>

            {(inputError || errorMessage) && <Label pointing negative prompt> {inputError || errorMessage}</Label>}
        </div>
    )
};